import { z } from 'zod';
/**
 * Rules (DESIGN-server §2.2, spec §2.1/§2.4). Every default is House Rules.
 *
 * Note (zod v4): `.default(x)` returns `x` verbatim without parsing, so nested objects use
 * `.prefault({})` to have their own field defaults applied.
 */
export const FormatSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('quick'), hands: z.literal(4) }),
    z.object({ kind: z.literal('classic'), target: z.union([z.literal(500), z.literal(300)]) }),
    z.object({ kind: z.literal('money') }), // single hand
]);
/**
 * Clocks, in ms. Raised from 20/10/3 after a playtest: the table was auto-playing for people who
 * were still reading their hand, and a card game that plays itself is not a card game. A human
 * needs long enough to look at thirteen cards and decide, not long enough to make three other
 * people wait — 30 s to bid and 15 s a card is that line, and the time bank now spends itself
 * before the server ever plays a card for a connected player.
 */
export const TimersSchema = z.object({
    bidMs: z.number().default(30_000),
    playMs: z.number().default(15_000),
    /** One legal card: there is nothing to decide, so this is a beat to notice, not a turn. */
    forcedMs: z.number().default(5_000),
    timeBankMs: z.number().default(15_000),
    timeBanksPerHand: z.number().default(2),
    trickHoldMs: z.number().default(1_800),
});
const CardIdSchema = z.number().int().min(0).max(53);
/** Test-only fixture: 4 hands of 13 cards, dealt in seat order instead of shuffling. */
export const TestFixtureSchema = z.object({
    hands: z.array(z.array(CardIdSchema).length(13)).length(4),
});
export const RulesSchema = z.object({
    jokers: z.boolean().default(true), // off => classic 52-card, A♠ high
    deucesHigh: z.boolean().default(true), // 2♦ > 2♠ > A♠
    nil: z.boolean().default(true),
    blindNil: z.boolean().default(true), // only when trailing by >= blindNilDeficit
    blindNilDeficit: z.number().int().default(100),
    board: z.boolean().default(true), // team contract min 4
    boardMin: z.number().int().default(4),
    tenFor200: z.boolean().default(false),
    bagsPenalty: z.boolean().default(true), // 10 bags = -100
    bagsThreshold: z.number().int().default(10),
    bostonBonus: z.number().int().default(200),
    breakSpades: z.boolean().default(true),
    failedNilTricks: z.enum(['bags', 'helpTeam']).default('bags'),
    bidding: z.enum(['simultaneous', 'sequential']).default('simultaneous'),
    format: FormatSchema.default({ kind: 'quick', hands: 4 }),
    timers: TimersSchema.prefault({}),
    __test: TestFixtureSchema.optional(),
});
/** What clients may send over the wire: everything except the test fixture hook. */
export const WireRulesSchema = RulesSchema.omit({ __test: true });
export const HOUSE_RULES = RulesSchema.parse({});
/** Blitz: the same game with the thinking time cut roughly in third, not with it removed. */
export const BLITZ_TIMERS = { bidMs: 20_000, playMs: 10_000 };
/** Merge a partial rules object over House Rules (throws on invalid input). */
export const resolveRules = (input) => RulesSchema.parse(input ?? {});
//# sourceMappingURL=rules.js.map